// Mermaid diagram renderer with pan/zoom and dynamic CDN load.
// The mermaid library is loaded from jsdelivr on first render so it never
// enters the Vite bundle. A network failure falls back to showing the raw
// source in a <details> so the page never crashes.

import { useEffect, useId, useRef, useState, useCallback } from "react";
import type { CSSProperties } from "react";

interface MermaidLib {
  initialize: (cfg: Record<string, unknown>) => void;
  render: (id: string, src: string) => Promise<{ svg: string }>;
}

let mermaidPromise: Promise<MermaidLib> | null = null;

async function loadMermaid(): Promise<MermaidLib> {
  if (mermaidPromise) return mermaidPromise;
  mermaidPromise = (async () => {
    // Dynamic import from CDN — kept out of the bundle.
    const url = "https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.esm.min.mjs";
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mod: any = await import(/* @vite-ignore */ /* webpackIgnore: true */ url);
    const mermaid = (mod as { default: MermaidLib }).default;
    mermaid.initialize({
      startOnLoad: false,
      securityLevel: "strict",
      theme: "base",
      themeVariables: {
        primaryColor: "#c66a3e",
        primaryTextColor: "#1c1410",
        primaryBorderColor: "#a8552f",
        lineColor: "#5b4234",
        secondaryColor: "#3f5d4a",
        tertiaryColor: "#efe6d6",
        background: "#f7efe1",
        fontFamily: "Geist, system-ui, sans-serif",
      },
      flowchart: { curve: "basis", htmlLabels: true },
    });
    return mermaid;
  })().catch((e) => {
    mermaidPromise = null;
    throw e;
  });
  return mermaidPromise;
}

export interface MermaidDiagramProps {
  source: string;
  title: string;
  caption?: string;
  notes?: string;
}

export function MermaidDiagram({ source, title, caption, notes }: MermaidDiagramProps) {
  const id = useId().replace(/:/g, "_");
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const dragRef = useRef<{ x: number; y: number } | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const reduceMotion = useRef(
    typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
  );

  useEffect(() => {
    let cancelled = false;
    setSvg(null);
    setError(null);
    loadMermaid()
      .then(async (mermaid) => {
        try {
          const { svg } = await mermaid.render(`m_${id}`, source);
          if (!cancelled) setSvg(svg);
        } catch (e) {
          if (!cancelled) setError(e instanceof Error ? e.message : String(e));
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load mermaid");
      });
    return () => {
      cancelled = true;
    };
  }, [source, id]);

  const fit = useCallback(() => {
    setScale(1);
    setTx(0);
    setTy(0);
  }, []);

  const zoom = useCallback((delta: number, cx?: number, cy?: number) => {
    setScale((s) => {
      const next = Math.min(4, Math.max(0.3, +(s + delta).toFixed(2)));
      if (cx !== undefined && cy !== undefined && stageRef.current) {
        const rect = stageRef.current.getBoundingClientRect();
        const ox = cx - rect.left - rect.width / 2;
        const oy = cy - rect.top - rect.height / 2;
        const ratio = next / s - 1;
        setTx((t) => t - ox * ratio);
        setTy((t) => t - oy * ratio);
      }
      return next;
    });
  }, []);

  // Wheel zoom
  const onWheel = (e: React.WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey && Math.abs(e.deltaY) < 4) return;
    e.preventDefault();
    zoom(e.deltaY > 0 ? -0.1 : 0.1, e.clientX, e.clientY);
  };

  // Drag pan (mouse + touch)
  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    dragRef.current = { x: e.clientX - tx, y: e.clientY - ty };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    setTx(e.clientX - dragRef.current.x);
    setTy(e.clientY - dragRef.current.y);
  };
  const onPointerUp = () => {
    dragRef.current = null;
  };

  // Keyboard
  const onKey = (e: React.KeyboardEvent) => {
    const step = 24;
    switch (e.key) {
      case "ArrowUp": setTy((t) => t + step); e.preventDefault(); break;
      case "ArrowDown": setTy((t) => t - step); e.preventDefault(); break;
      case "ArrowLeft": setTx((t) => t + step); e.preventDefault(); break;
      case "ArrowRight": setTx((t) => t - step); e.preventDefault(); break;
      case "+": case "=": zoom(0.1); e.preventDefault(); break;
      case "-": case "_": zoom(-0.1); e.preventDefault(); break;
      case "f": case "F": fit(); e.preventDefault(); break;
      case "Escape": if (fullscreen) setFullscreen(false); break;
    }
  };

  const stageStyle: CSSProperties = fullscreen
    ? { position: "fixed", inset: 0, zIndex: 1000, background: "var(--md-sc-lowest)", padding: 24 }
    : {};

  return (
    <figure className={`lex-diagram${fullscreen ? " lex-diagram--full" : ""}`} style={stageStyle}>
      <div className="lex-diagram-toolbar" role="toolbar" aria-label={`${title} controls`}>
        <button type="button" onClick={() => zoom(0.1)} aria-label="Zoom in">+</button>
        <button type="button" onClick={() => zoom(-0.1)} aria-label="Zoom out">−</button>
        <button type="button" onClick={fit} aria-label="Fit diagram">Fit</button>
        <button type="button" onClick={() => setFullscreen((v) => !v)} aria-label={fullscreen ? "Exit fullscreen" : "Fullscreen"}>
          {fullscreen ? "Exit" : "Full"}
        </button>
        <span className="lex-diagram-toolbar__scale" aria-live="polite">{Math.round(scale * 100)}%</span>
      </div>
      <div
        ref={stageRef}
        className="lex-diagram__stage"
        role="img"
        aria-label={title}
        tabIndex={0}
        onKeyDown={onKey}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {!svg && !error && <div className="lex-diagram__skeleton" aria-hidden="true">Loading diagram…</div>}
        {error && (
          <div className="lex-diagram__error" role="alert">
            <p>Could not render diagram. The Mermaid library failed to load (offline?) or the source is invalid.</p>
            <details>
              <summary>View source</summary>
              <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{source}</pre>
            </details>
            <p style={{ font: "var(--t-body-sm)", color: "var(--md-on-surface-var)" }}>{error}</p>
          </div>
        )}
        {svg && (
          <div
            className="lex-diagram__svg"
            style={{
              transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
              transformOrigin: "center center",
              transition: reduceMotion.current ? "none" : "transform 120ms ease-out",
              willChange: "transform",
            }}
            dangerouslySetInnerHTML={{ __html: svg }}
          />
        )}
      </div>
      {(caption || notes) && (
        <figcaption className="lex-diagram__caption">
          {caption}
          {notes && <span className="lex-diagram__notes"> — {notes}</span>}
        </figcaption>
      )}
    </figure>
  );
}

export default MermaidDiagram;
